import HmacSHA256 from 'crypto-js/hmac-sha256';
import AES from 'crypto-js/aes';
import PBKDF2 from 'crypto-js/pbkdf2';
import Hex from 'crypto-js/enc-hex';
import Base64 from 'crypto-js/enc-base64';
import Utf8 from 'crypto-js/enc-utf8';
import WordArray from 'crypto-js/lib-typedarrays';
import type CryptoJS from 'crypto-js';

export const PBKDF2_ITER = 310_000;
const IV_BYTES   = 16;
export const ENC_PREFIX = "enc::";

export const hex = (wa: CryptoJS.lib.WordArray) => wa.toString();
export const parseHex = (s: string) => Hex.parse(s);
export const randomHex = (bytes = 32) => WordArray.random(bytes).toString();

export const deriveKey = (pw: string, saltHex: string, iter = PBKDF2_ITER): CryptoJS.lib.WordArray =>
  PBKDF2(pw, parseHex(saltHex), { keySize: 256/32, iterations: iter });

export const encryptAES = (plain: string, key: CryptoJS.lib.WordArray) => {
  const iv = WordArray.random(IV_BYTES);
  const ct = AES.encrypt(plain, key, { iv }).ciphertext;
  const mac = HmacSHA256(iv.clone().concat(ct), key);
  return { iv: hex(iv), ct: Base64.stringify(ct), mac: hex(mac) };
};
export const decryptAES = (o: {iv:string;ct:string;mac:string}, key: CryptoJS.lib.WordArray) => {
  const iv = parseHex(o.iv); const ct = Base64.parse(o.ct);
  const chk = HmacSHA256(iv.clone().concat(ct), key);
  if (hex(chk)!==o.mac) throw new Error("HMAC mismatch");
  const params = { ciphertext: ct } as CryptoJS.lib.CipherParams;
  return AES.decrypt(params, key, { iv }).toString(Utf8);
};

export const encryptForStorage = (plain: string, key: CryptoJS.lib.WordArray) => ENC_PREFIX+JSON.stringify(encryptAES(plain,key));
export const decryptFromStorage = (payload: string, key: CryptoJS.lib.WordArray) => payload.startsWith(ENC_PREFIX)?decryptAES(JSON.parse(payload.slice(ENC_PREFIX.length)),key):payload;

/**
 * SHA-256 hash using Web Crypto API (industry-standard)
 * Returns lowercase hex digest
 */
export async function sha256Hex(input: string): Promise<string> {
  const data = new TextEncoder().encode(input);
  const hashBuf = await crypto.subtle.digest('SHA-256', data);
  const hashArr = Array.from(new Uint8Array(hashBuf));
  return hashArr.map(b => b.toString(16).padStart(2, '0')).join('');
}
